import { useEffect, useState, useCallback } from "react";
import api, { apiErr, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SCORE } from "@/constants/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { Download, ShieldCheck, EyeOff, Sprout, Lock, FileText, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const BAND_STYLE = {
  advanced: "bg-forest-mid text-white border-forest-dark",
  strong: "bg-emerald-100 text-emerald-900 border-emerald-300",
  developing: "bg-amber-100 text-amber-900 border-amber-300",
  early_progress: "bg-orange-100 text-orange-900 border-orange-300",
  priority_improvement: "bg-rose-100 text-rose-900 border-rose-300",
  restricted: "bg-rose-600 text-white border-rose-800",
};
const fmt1 = (v) => (v == null ? "—" : Number(v).toFixed(1));

export default function ScoreReport({ periodId, open, onClose, canInternal, canApproveDisclosure, onDisclosureChange }) {
  const { user } = useAuth();
  const [edition, setEdition] = useState(canInternal ? "internal" : "public");
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (ed) => {
    if (!periodId) return;
    setLoading(true);
    try {
      const r = await api.get(`/score/periods/${periodId}/report?edition=${ed}`);
      setRep(r.data);
      setEdition(r.data.edition);
    } catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  }, [periodId]);

  useEffect(() => { if (open) load(canInternal ? "internal" : "public"); }, [open, load, canInternal]);

  const download = () =>
    downloadFile(`/score/periods/${periodId}/report.pdf?edition=${edition}`, `roots-report-${edition}.pdf`)
      .catch(() => toast.error("PDF export failed"));

  const setDisclosure = async (approved) => {
    try {
      await api.post(`/score/periods/${periodId}/report/disclosure`, { approved });
      toast.success(approved ? "Approved for public disclosure" : "Public disclosure revoked");
      await load(edition);
      onDisclosureChange?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const disc = rep?.public_disclosure || {};
  const approved = !!disc.approved;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-0" data-testid={SCORE.reportModal}>
        <DialogHeader className="sticky top-0 z-10 flex flex-row items-center justify-between border-b bg-white px-5 py-3">
          <DialogTitle className="flex items-center gap-2 font-heading text-base"><FileText className="h-4 w-4 text-forest-mid" /> Sustainability Report</DialogTitle>
          <div className="mr-6 flex items-center gap-2">
            {canInternal && (
              <div className="flex rounded-lg border border-sage-light p-0.5 text-xs">
                <button onClick={() => load("internal")} data-testid={SCORE.reportEdition("internal")}
                  className={`rounded px-2 py-1 font-medium ${edition === "internal" ? "bg-forest-mid text-white" : "text-muted-foreground"}`}>Internal</button>
                <button onClick={() => load("public")} data-testid={SCORE.reportEdition("public")}
                  className={`rounded px-2 py-1 font-medium ${edition === "public" ? "bg-forest-mid text-white" : "text-muted-foreground"}`}>Public</button>
              </div>
            )}
            <Button size="sm" variant="outline" onClick={download} data-testid={SCORE.reportDownload}><Download className="mr-1 h-4 w-4" /> PDF</Button>
          </div>
        </DialogHeader>

        {loading || !rep ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Preparing report…</div>
        ) : (
          <div className="space-y-5 p-6">
            {/* public disclosure banner */}
            {rep.is_public && (
              <div className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border-l-4 p-3 ${approved ? "border-emerald-500 bg-emerald-50/70" : "border-amber-400 bg-amber-50/70"}`}>
                <p className={`flex items-center gap-1.5 text-xs ${approved ? "text-emerald-900" : "text-amber-900"}`}>
                  {approved ? <ShieldCheck className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  {approved ? `Approved for disclosure${disc.approved_by_name ? ` by ${disc.approved_by_name}` : ""}` : "Unlisted draft · noindex · not for publication until the asset owner approves disclosure"}
                </p>
                {canApproveDisclosure && (
                  approved
                    ? <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDisclosure(false)} data-testid={SCORE.disclosureRevoke}>Revoke</Button>
                    : <Button size="sm" className="h-7 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={() => setDisclosure(true)} data-testid={SCORE.disclosureApprove}>Approve disclosure</Button>
                )}
              </div>
            )}

            {/* branded header */}
            <div className="rounded-xl border border-sage-light bg-sage-surface p-5">
              <div className="flex items-center gap-2 text-forest-dark">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-forest-mid"><Sprout className="h-4 w-4 text-white" /></div>
                <div>
                  <p className="font-heading text-sm font-bold">ROOTS Sustainability Report</p>
                  <p className="text-[11px] text-muted-foreground">{rep.tenant_name} · {rep.is_public ? "Public edition" : "Internal edition"}</p>
                </div>
              </div>
              <h2 className="mt-3 font-heading text-lg font-bold text-forest-dark">{rep.contract_name}</h2>
              <p className="text-xs text-muted-foreground">{rep.client_owner ? `${rep.client_owner} · ` : ""}{rep.period_label} ({fmtDate(rep.period_start)}–{fmtDate(rep.period_end)})</p>

              <div className="mt-4 flex items-end gap-3">
                <span className="font-heading text-5xl font-bold text-forest-dark">
                  {rep.overall_display != null ? fmt1(rep.overall_display) : rep.range ? `${fmt1(rep.range.lower)}–${fmt1(rep.range.upper)}` : "—"}
                </span>
                <span className="mb-1 text-sm text-muted-foreground">/ 100</span>
                <span className={`mb-1 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${BAND_STYLE[rep.band_key] || "bg-slate-100 text-slate-700 border-slate-200"}`} data-testid="score-report-band">
                  {rep.gate === "restricted" && <Lock className="h-3 w-3" />}{rep.band_label || "Not scored"}
                </span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">{rep.profile_version} · method {rep.method_version} · issued {fmtDate(rep.issued_at)}{rep.issued_by_name ? ` by ${rep.issued_by_name}` : ""}</p>
            </div>

            <p className="text-sm leading-relaxed text-forest-dark">{rep.narrative}</p>

            {/* pillars */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pillar performance</p>
              <div className="space-y-2">
                {rep.pillars.map((p) => (
                  <div key={p.key} className="flex items-center gap-3">
                    <span className="w-28 shrink-0 text-sm text-forest-dark">{p.label}</span>
                    <div className="h-2.5 flex-1 rounded-full bg-slate-100"><div className="h-2.5 rounded-full bg-forest-mid" style={{ width: `${Math.min(100, p.score ?? 0)}%` }} /></div>
                    <span className="w-16 shrink-0 text-right text-sm font-semibold text-forest-dark">{fmt1(p.score)} <span className="text-[10px] font-normal text-muted-foreground">/{p.budget}%</span></span>
                  </div>
                ))}
              </div>
            </div>

            {/* internal-only detail */}
            {!rep.is_public && (
              <>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full border border-sage-light bg-white px-2.5 py-0.5 font-semibold text-forest-dark">Coverage C {fmt1(rep.coverage_display)}%</span>
                  <span className="rounded-full border border-sage-light bg-white px-2.5 py-0.5 font-semibold text-forest-dark">Evidence Q {fmt1(rep.evidence_index)}</span>
                  <span className="rounded-full border border-sage-light bg-white px-2.5 py-0.5 font-semibold text-forest-dark">Gate {rep.gate}</span>
                  {rep.prior && (
                    <span className={`inline-flex items-center gap-0.5 rounded-full border px-2.5 py-0.5 font-semibold ${rep.prior.delta >= 0 ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
                      {rep.prior.delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {rep.prior.delta > 0 ? "+" : ""}{fmt1(rep.prior.delta)} vs {rep.prior.label}
                    </span>
                  )}
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">KPI contributions</p>
                  <div className="overflow-x-auto rounded-lg border border-sage-light">
                    <Table>
                      <TableHeader><TableRow><TableHead>KPI</TableHead><TableHead>Score</TableHead><TableHead>Eff. wt</TableHead><TableHead>Contribution</TableHead><TableHead>Q</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {rep.kpi_table.map((r) => (
                          <TableRow key={r.code}>
                            <TableCell className="text-xs"><span className="font-mono text-muted-foreground">{r.code}</span> {r.name}</TableCell>
                            <TableCell className="text-xs font-semibold">{fmt1(r.kpi_score)}</TableCell>
                            <TableCell className="text-xs">{Number(r.effective_weight).toFixed(2)}</TableCell>
                            <TableCell className="text-xs font-semibold text-forest-dark">{r.contribution == null ? "—" : Number(r.contribution).toFixed(2)}</TableCell>
                            <TableCell className="text-xs">{r.evidence_quality}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}

            <p className="border-t border-sage-light pt-3 text-[11px] text-muted-foreground">
              Issued and frozen under the ROOTS method. “Issued” is not the same as “published” or externally assured. Every figure is reproducible from the recorded evidence.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
